import { SpsIllustrations } from "@spscommerce/ds-illustrations";
import {
    SpsButton,
    SpsLabel,
    SpsTextInput,
    SpsValidators,
    useSpsForm,
} from "@spscommerce/ds-react";
import { ButtonKind } from "@spscommerce/ds-shared";
import { useSpsLanguage } from "@spscommerce/i18n";
import React from "react";
import StatusState from "../../components/StatusState";
import { useLocation } from "react-router-dom";
import { AppRouteProps } from "../../App";

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export type ForwardInviteDetails = {
    first_name: string;
    last_name: string;
    email: string;
    signed_invite: string;
};

export type RequestInvitationProps = Partial<AppRouteProps> & Pick<AppRouteProps, "inviteService">;

export function RequestInvitation({ inviteService }: RequestInvitationProps) {
    const [requestSentSuccessfully, setRequestSentSuccessfully] = React.useState(false);
    const [requestError, setRequestError] = React.useState<null | string>(null);
    const [submitting, setSubmitting] = React.useState(false);

    const { t } = useSpsLanguage();

    const { formValue, formMeta } = useSpsForm(
        {
            firstName: "",
            lastName: "",
            email: "",
        },
        {
            firstName: [SpsValidators.required],
            lastName: [SpsValidators.required],
            email: [SpsValidators.required, SpsValidators.pattern(/.+@.+\..+/)],
        }
    );

    function disableSubmitButton() {
        return (
            formMeta.fields.firstName.hasError("required") ||
            formMeta.fields.lastName.hasError("required") ||
            formMeta.fields.email.hasError("required") ||
            formMeta.fields.email.hasError("pattern")
        );
    }

    const queryParams = useQuery();
    // get returns null if prop is undefined, to avoid TS errors
    // we need to set up empty string if null
    const signed_invite_id = queryParams.get("signed_invite") || "";

    async function requestInvitation(e: React.FormEvent) {
        setSubmitting(true);

        const details: ForwardInviteDetails = {
            first_name: formValue.firstName,
            last_name: formValue.lastName,
            email: formValue.email,
            signed_invite: signed_invite_id,
        };

        try {
            await inviteService.forwardInvite(details);
            setRequestSentSuccessfully(true);
        } catch (error) {
            console.error("failed to send invitation request", error);

            const e = error as any;
            const errorData = e?.response?.data;
            let errorMessage;
            if (typeof errorData?.error_description === "string") {
                if (
                    errorData.error_description ===
                    "Original user is no longer a member of the organization"
                ) {
                    errorMessage = t("cpauth.requestInvite.error.userNotAMember", {
                        defaultValue: "Original user is no longer a member of the organization",
                    });
                } else if (
                    errorData.error_description ===
                    "Original user doesn't have permissions to forward invitation."
                ) {
                    errorMessage = t("cpauth.requestInvite.error.userPermissions", {
                        defaultValue:
                            "Original user doesn't have permissions to forward invitation.",
                    });
                } else {
                    errorMessage = errorData.error_description;
                }
            } else if (typeof errorData?.error_description?.[0] === "string") {
                if (
                    errorData.error_description[0] ===
                    "Unable to forward invitation to the same email"
                ) {
                    errorMessage = t("cpauth.requestInvite.error.sameEmail", {
                        defaultValue: "Unable to forward invitation to the same email",
                    });
                } else if (
                    errorData.error_description[0] ===
                    "Unable to forward invitation, invite has already been forwarded"
                ) {
                    errorMessage = t("cpauth.requestInvite.error.alreadyForwarded", {
                        defaultValue:
                            "Unable to forward invitation, invite has already been forwarded",
                    });
                } else {
                    errorMessage = errorData.error_description[0];
                }
            } else if (typeof errorData?.error_description?.details?.email?.[0] === "string") {
                if (
                    errorData.error_description.details.email[0] ===
                    "A user with this email already exists."
                ) {
                    errorMessage = t("cpauth.requestInvite.error.emailExists", {
                        defaultValue: "A user with this email already exists.",
                    });
                } else {
                    errorMessage = errorData.error_description.details.email[0];
                }
            } else {
                t("cpauth.genericErrorDescription", {
                    defaultValue:
                        "Please go back and try again or contact support if the problem persists.",
                });
            }

            setRequestError(errorMessage);
        }

        setSubmitting(false);
    }

    return (
        <>
            <div className="auth-box">
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
                <div className="auth-content">
                    {requestError && (
                        <StatusState
                            header={t("cpauth.createaccount.failedToSendInvitation", {
                                defaultValue: "Failed to send Invitation Request",
                            })}
                            message={requestError}
                            imgSrc={SpsIllustrations.Large.BROWSER_ERROR}
                            ignorePublicUrl
                            alt=""
                            supportInfo
                        />
                    )}
                    {requestSentSuccessfully && (
                        <StatusState
                            header={t("cpauth.createaccount.invitationSent", {
                                defaultValue: "Invitation Sent",
                            })}
                            message={t("cpauth.createaccount.invitationSent.info", {
                                defaultValue: "Check your inbox for a link to create your account",
                            })}
                            imgSrc={SpsIllustrations.Large.PAPER_PLANE}
                            ignorePublicUrl
                            alt=""
                        />
                    )}
                    {!requestError && !requestSentSuccessfully && (
                        <>
                            <h2 className="auth-content-header">
                                {t("cpauth.createaccount.requestInvitation", {
                                    defaultValue: "Request an Invitation",
                                })}
                            </h2>

                            <SpsLabel for={formMeta.fields.firstName}>
                                {t("cpauth.firstname", {
                                    defaultValue: "First Name",
                                })}
                            </SpsLabel>
                            <SpsTextInput
                                className="mb-2"
                                value={formValue.firstName}
                                formMeta={formMeta.fields.firstName}
                                data-testid="first-name-input"
                            />

                            <SpsLabel for={formMeta.fields.lastName}>
                                {t("cpauth.lastname", {
                                    defaultValue: "Last Name",
                                })}
                            </SpsLabel>
                            <SpsTextInput
                                className="mb-2"
                                value={formValue.lastName}
                                formMeta={formMeta.fields.lastName}
                                data-testid="last-name-input"
                            />

                            <SpsLabel for={formMeta.fields.email}>
                                {t("cpauth.email", {
                                    defaultValue: "Email Address",
                                })}
                            </SpsLabel>
                            <SpsTextInput
                                className="mb-2"
                                value={formValue.email}
                                formMeta={formMeta.fields.email}
                                data-testid="email-input"
                            />

                            <div>
                                <SpsButton
                                    className="mr-1 mb-1 mt-1 auth-button"
                                    disabled={disableSubmitButton()}
                                    spinning={submitting}
                                    kind={ButtonKind.CONFIRM}
                                    onClick={requestInvitation}
                                >
                                    {t("cpauth.createaccount.sendInviteLink", {
                                        defaultValue: "Send Me an Invite Link",
                                    })}
                                </SpsButton>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
